import React, {useMemo} from 'react';
import {Dropdown} from '@/components/atoms';
import PropTypes from 'prop-types';
import './DropdownControl.scss';

const DropdownControl = (props) => {
  /**
   * @typedef {Object} Props
   * @property {function} handleChange - Function to handle changes in the dropdown.
   * @property {string} label - Label for the dropdown control.
   * @property {string} path - Path for the dropdown control.
   * @property {Object} errors - Object containing validation errors.
   * @property {Object} schema - Schema for the dropdown control.
   * @property {Object} uischema - UI schema for the dropdown control.
   * @property {boolean} required - Indicates if the dropdown is required.
   */
  const {handleChange, label, path, errors, schema, uischema, required, data} =
    props;
  const dropdownChange = (newVal) => {
    const selection = uischema?.selection || 'single';
    if (selection === 'multiple') {
      handleChange(path, newVal);
    } else {
      handleChange(path, newVal?.[0]?.displayText ?? '');
      handleChange(path + 'Value', newVal?.[0]?.value ?? '');
    }
  };

  const values = useMemo(() => {
    if (Array.isArray(data)) return data;

    // Return empty array if data is falsy (null, undefined, empty string)
    // This prevents showing "Invalid" in dropdown when no value is selected
    if (!data || data === '') return [];

    // Check if the data value exists in the schema options
    // If not found, return empty array to avoid showing "Invalid"
    const options = schema?.options || [];
    const matchingOption = options.find(
      (opt) =>
        opt?.displayText === data || opt?.value === data || opt?.id === data
    );

    if (!matchingOption && options.length > 0) {
      return [];
    }

    return [{id: data, displayText: data}];
  }, [data, schema?.options]);

  return (
    <Dropdown
      label={label}
      updateValue={dropdownChange}
      errors={errors}
      schema={schema}
      uiSchema={uischema}
      required={required}
      value={values}
    />
  );
};

DropdownControl.propTypes = {
  handleChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  errors: PropTypes.string,
  schema: PropTypes.shape({
    options: PropTypes.array
  }).isRequired,
  uischema: PropTypes.shape({
    selection: PropTypes.string
  }),
  required: PropTypes.bool,
  data: PropTypes.any
};

export default React.memo(DropdownControl);
